<template>
    <div class="password-reset guided-steps-page">
        <title-screen title ="21"></title-screen>
        <el-form class="form mt2" ref="passwordReset" :model="passwordReset" :rules="rules" @submit.native.prevent="submitForm('passwordReset')">
            <div class="messages">
                <span class="error" v-html="messages"></span>
                <span class="error" v-if="urlExpired">再度「<a class="url-expired-message" href="#" @click="openLoginDialog">パスワードを忘れた方はこちら</a>」から設定をお願い致します。</span>
            </div>
            <!-- Guided steps (backlog #17, same behaviour as 本人確認 #13): ① the new password,
                 ending with the send button, which first checks ①'s fields; ② the code + submit,
                 dimmed AND locked until a code is sent in this page visit. The server flow is
                 unchanged (the code always goes to the reset row's own send_to). -->
            <div v-if="verifyPwReset" class="iv-steps">
                <section :class="['iv-step', codeSent ? 'is-done' : 'is-active']">
                    <div class="iv-step__marker">
                        <span class="iv-step__number">
                            <i v-if="codeSent" class="el-icon-check" aria-hidden="true"></i>
                            <template v-else>1</template>
                        </span>
                    </div>
                    <div class="iv-step__body">
                        <h2 class="iv-step__title">新しいパスワードを入力</h2>
                        <table class="table-register">
                            <tr class="password-row">
                                <th rowspan="2" id="name" class="column-label required">新しいパスワード</th>
                                <td class="column-input">
                                  <div class="d-flex">
                                    <el-form-item class="mr-auto password-input" prop="password1">
                                        <input type="password" v-model="passwordReset.password1" placeholder="パスワード" class="input">
                                        <span class="d-inline-block small-text">小文字と数字を含めて8～32文字で組み合わせて設定してください</span>
                                    </el-form-item>
                                    <span class="ml-3 small-text">※パスワードの設定と管理には十分にお気をつけください。</span>
                                  </div>
                                  <div v-if="isPassword1Err" style="height: 20px"></div>
                                </td>
                            </tr>
                            <tr class="password-confirm-row">
                                <td class="column-input">
                                  <div class="d-flex">
                                    <el-form-item class="mr-auto password-input" prop="password2"><input type="password" v-model="passwordReset.password2" placeholder="パスワード" class="input"></el-form-item>
                                    <div class="ml-3 small-text">※確認のためもう一度、コピーせず直接入力してください。</div>
                                  </div>
                                </td>
                            </tr>
                        </table>
                        <div class="iv-step__action">
                            <el-button type="primary" class="send-code-button" @click="handleSendVerificationCode" :disabled="verificationCodeCooldown > 0">本人確認コードを送信</el-button>
                            <span v-if="verificationCodeCooldown > 0" class="d-inline-block small-text">再送信可能まで30秒</span>
                        </div>
                    </div>
                </section>
                <section :class="['iv-step', codeSent ? 'is-active' : 'is-pending']">
                    <div class="iv-step__marker">
                        <span class="iv-step__number">2</span>
                    </div>
                    <div class="iv-step__body">
                        <h2 class="iv-step__title">本人確認コードを入力</h2>
                        <table class="table-register">
                            <tr>
                                <th class="column-label required">本人確認コード</th>
                                <td class="column-input max-width-400">
                                    <el-form-item prop="verificationCode">
                                        <el-input class="input verification-code-input" v-model="passwordReset.verificationCode" :disabled="!codeSent" placeholder="本人確認コード"></el-input>
                                        <span v-if="!codeSent" class="d-inline-block small-text">先に本人確認コードを送信してください</span>
                                    </el-form-item>
                                </td>
                            </tr>
                        </table>
                        <div class="iv-step__action text-center">
                            <el-button native-type="submit" type="button" :disabled="!codeSent">パスワードを再設定する</el-button>
                        </div>
                    </div>
                </section>
            </div>
        </el-form>
    </div>
</template>

<script>
    import TitleScreen from '@/components/common/TitleScreen'
    import ApiPasswordRemind from '@/api/passwordremind'
    import { SET_OPEN_LOGIN_DIALOG } from '@/store/actions.type'
    import verificationCodeCooldownMixin from '@/mixins/verificationCodeCooldownMixin'

    const PASSWORD_MISMATCH_MESSAGE = '確認パスワードが一致しません'

    export default {
        name: 'password-reset',
        mixins: [verificationCodeCooldownMixin],
        components: {
            'title-screen': TitleScreen
        },
        created () {
            var code = this.$route.query.code
            ApiPasswordRemind.sendCode(code).then(response => {
                if (response.status === 200) {
                    this.verifyPwReset = true
                }
            }).catch(err => {
                var status = err.response.status
                if (status === 401) {
                    this.messages = 'アクセストークンの有効期限切れです。'
                } else if (status === 403) {
                    this.messages = 'URLが無効です。'
                } else if (status === 400) {
                    this.messages = 'URLが無効です。'
                } else if (status === 410) {
                    this.messages = '認証URLの有効期限が過ぎました。'
                    this.urlExpired = true
                } else {
                    this.messages = 'URLが無効です。'
                }
            })
        },
        data () {
            return {
                passwordReset: {
                    password1: '',
                    password2: '',
                    verificationCode: ''
                },
                accessTime: new Date(),
                messages: '',
                urlExpired: false,
                verifyPwReset: false,
                isPassword1Err: false,
                // Set once a code is sent successfully in this page visit; unlocks step ②.
                codeSent: false,
                rules: {
                    password1: [
                        {
                            required: true,
                            // PasswordPolicy.kt: 8+ chars, lowercase+digit required, "¥" forbidden.
                            pattern: /^(?=.*[a-z])(?=.*\d)[^¥]{8,32}$/,
                            message: '小文字と数字を含めて8文字以上で入力してください',
                            trigger: 'blur'
                        }
                    ],
                    password2: [
                        {
                            required: true,
                            message: this.$t('rules.required', { key: this.$t('member.password') }),
                            trigger: 'blur'
                        }
                    ],
                    verificationCode: [
                        {
                            required: true,
                            message: '本人確認コードを入力してください',
                            trigger: 'blur'
                        }
                    ]
                }
            }
        },
        methods: {
            handleSendVerificationCode () {
                if (this.verificationCodeCooldown > 0) {
                    this.$message({
                        message: '本人確認コードは30秒に1回のみ送信できます',
                        type: 'error',
                        duration: 3000
                    })
                    return
                }
                // Step ① must be complete before a code is sent (same as 本人確認, #13).
                return this.validateStepOne().then(valid => {
                    if (!valid) {
                        return
                    }
                    return this.sendVerificationCode()
                })
            },
            validateStepOne () {
                const form = this.$refs['passwordReset']
                const check = prop => new Promise(resolve => {
                    form.validateField(prop, errorMessage => resolve(!errorMessage))
                })
                return Promise.all([check('password1'), check('password2')]).then(([password1Ok, password2Ok]) => {
                    this.isPassword1Err = !password1Ok
                    if (!password1Ok || !password2Ok) {
                        return false
                    }
                    // This screen has no match rule on password2; submitForm checks it with this
                    // same message, so step ① does too.
                    if (this.passwordReset.password1 !== this.passwordReset.password2) {
                        this.messages = PASSWORD_MISMATCH_MESSAGE
                        return false
                    }
                    if (this.messages === PASSWORD_MISMATCH_MESSAGE) {
                        this.messages = ''
                    }
                    return true
                })
            },
            sendVerificationCode () {
                // 送信先はresetCodeに紐づくsend_toにサーバー側で解決される - クライアントからは
                // 送信先を渡さない
                return ApiPasswordRemind.sendPasswordResetOtc(this.$route.query.code).then(() => {
                    this.codeSent = true
                    this.$message({
                        message: 'お客様に本人確認コードを送信しました',
                        type: 'success',
                        duration: 3000
                    })
                    this.startVerificationCodeCooldown()
                }).catch(err => {
                    var status = err.response.status
                    if (status === 410) {
                        this.messages = '認証URLの有効期限が過ぎました。'
                        this.urlExpired = true
                    } else {
                        this.messages = 'URLが無効です。'
                    }
                })
            },
            submitForm (formName) {
                // Enter in a step-① field submits the form natively; nothing to submit until
                // step ② is unlocked.
                if (!this.codeSent) {
                    return
                }
                this.$refs[formName].validate((valid, object) => {
                    if (valid) {
                        if (this.passwordReset.password1 !== this.passwordReset.password2) {
                            this.messages = PASSWORD_MISMATCH_MESSAGE
                            return true
                        }
                        var diffBettweenAccessTimeAndNow =
                            Math.round((((new Date() - this.accessTime) % 86400000) % 3600000) / 60000)
                        if (diffBettweenAccessTimeAndNow > 60) {
                            this.messages = '認証URLの有効期限が過ぎました。'
                            this.urlExpired = true
                            this.verifyPwReset = false
                            return true
                        }
                        ApiPasswordRemind.sendResetPassword(this.$route.query.code, this.passwordReset.password1, this.passwordReset.verificationCode).then(response => {
                            if (response.status === 200) {
                                this.messages = 'パスワード再設定が完了しました。ログインしてください。'
                                this.verifyPwReset = false
                            }
                        }).catch(err => {
                            // 誤ったOTCは検証済み(消費不可)、正しいOTCは既に消費済み - どちらにせよ
                            // 古い値を残す意味がない (パスワード変更・本人確認画面と同様)
                            this.passwordReset.verificationCode = ''
                            var status = err.response.status
                            if (status === 401) {
                                this.messages = 'アクセストークンの有効期限切れです。'
                            } else if (status === 400 || status === 403 || status === 404) {
                                // この画面まで来ている以上、リセットリンク自体は着地時に検証済み -
                                // ここでの失敗は本人確認コードの誤りが圧倒的に多いため、その文言を出す。
                                // (バックエンドは403/404を返さなくなったが、念のため両方を受ける)
                                this.messages = '本人確認コードが間違っています。'
                            } else if (status === 422) {
                                // 本人確認コードのみが期限切れ - リセットリンク自体はまだ有効なので、
                                // urlExpired は立てない (立てると「再度パスワードを忘れた方はこちら
                                // から」と案内してしまい、使えるリンクを捨てさせることになる)。
                                // 同じ画面でコードを再送信すればそのまま続行できる。
                                this.messages = '本人確認コードの有効期限が切れています。再度コードを送信してください。'
                            } else if (status === 410) {
                                this.messages = '認証URLの有効期限が過ぎました。'
                                this.urlExpired = true
                            } else {
                                this.messages = 'URLが無効です。'
                            }
                        })
                    } else {
                        this.isPassword1Err = object.password1 !== null && object.password1 !== undefined
                    }
                })
            },
            openLoginDialog () {
                this.$store.dispatch(SET_OPEN_LOGIN_DIALOG, true)
            }
        }
    }
</script>

<style scoped>
    @import 'resetpassword.css';
    .member-edit {
        padding-bottom: 2em;
    }
    .url-expired-message {
        color: #009cad;
    }
</style>
<style>
@import '../../../../assets/css/guided_steps.css';
_:-ms-lang(x)::-ms-backdrop, .el-form input[type="password"] {
    line-height: normal;
}
</style>
